import React, { useEffect, useMemo, useState } from 'react';
import axiosPrivate from 'config/axiosPrivate';
import './UpgradePlanModal.css';
import { useTranslation } from 'react-i18next';
export const UPGRADE_PLANS = {
  light: { id: 'light', name: 'Light', pricePerMonth: 9.9 },
  premium: { id: 'premium', name: 'Premium', pricePerMonth: 49.9 },
  pro: { id: 'pro', name: 'PRO', pricePerMonth: 129 },
  business: { id: 'business', name: 'BUSINESS', pricePerMonth: 369 },
  entreprise: { id: 'entreprise', name: 'ENTREPRISE', pricePerMonth: 579 },
};

function formatEur(value) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}


export default function UpgradePlanModal({
  open,
  onClose,
  plan,
  supportHref = '/AdminClient/Support',
  minimumSiteCount = 1,
}) {
  const [siteCount, setSiteCount] = useState(1);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
const { t } = useTranslation();
  const normalizedMinimumSiteCount = Math.max(1, Number(minimumSiteCount) || 1);

  useEffect(() => {
    if (open && plan) {
      setSiteCount(normalizedMinimumSiteCount);
      setIsRedirecting(false);
      setPaymentError('');
    }
  }, [open, plan?.id, normalizedMinimumSiteCount]);

  const totals = useMemo(() => {
    if (!plan) return { subtotal: 0, line: '' };
    const subtotal = plan.pricePerMonth * siteCount;
    const siteLabel = `${t('site')}${siteCount > 1 ? 's' : ''}`;
    return {
      subtotal,
      line: `${siteCount} ${siteLabel} × ${formatEur(plan.pricePerMonth)} / ${t('mois')}`,
    };
  }, [plan, siteCount, t]);

  const bumpSites = (delta) => {
    setSiteCount((n) => Math.min(99, Math.max(normalizedMinimumSiteCount, n + delta)));
  };
  const canDecreaseSiteCount = siteCount > normalizedMinimumSiteCount;

  const handleGoToPayment = async () => {
    if (!plan?.id || isRedirecting) return;

    setIsRedirecting(true);
    setPaymentError('');

    try {
      const response = await axiosPrivate.get('/payment/api/subscriptions/generate-checkout-url', {
        params: {
          planKey: plan.id.toUpperCase(),
          quantity: siteCount,
        },
      });

      const checkoutUrl =
        response?.data?.checkoutUrl ||
        response?.data?.url ||
        response?.data?.data?.checkoutUrl ||
        response?.data?.data?.url ||
        (typeof response?.data === 'string' ? response.data : null);

      if (!checkoutUrl) {
        throw new Error('Checkout URL missing');
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      setIsRedirecting(false);
      setPaymentError("Impossible de lancer le paiement pour le moment. Merci de réessayer.");
    }
  };

  if (!open || !plan) return null;

  return (
    <div className="upm-overlay" onClick={onClose} role="presentation">
      <div
        className="upm-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upm-title"
      >
        <div className="upm-header">
          <div>
            <h2 id="upm-title" className="upm-title">
             {t('Passer au forfait ')} {plan.name}
            </h2>
            <p className="upm-desc">
              {t('Améliorez votre plan pour débloquer plus de contenus, plus de crédits et de capacités IA.')}
            </p>
          </div>
          <button type="button" className="upm-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>

        <div className="upm-config">
          <div className="upm-config-left">
            <div className="upm-config-icon" aria-hidden>
              <img src="/Images/website-icon.png" alt="" className="upm-config-icon-img" />
            </div>
            <div className="upm-config-label">{t('Nombre de site web')}</div>
          </div>
          <div className="upm-config-right">
            <span className="upm-config-unit">{formatEur(plan.pricePerMonth)} / {t('site')}</span>
            <div className="upm-stepper">
              {canDecreaseSiteCount && (
                <button type="button" className="upm-stepper-btn" onClick={() => bumpSites(-1)} aria-label="Diminuer">
                  −
                </button>
              )}
              <input
                className="upm-stepper-input"
                type="number"
                min={normalizedMinimumSiteCount}
                max={99}
                value={siteCount}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (Number.isNaN(v)) return;
                  setSiteCount(Math.min(99, Math.max(normalizedMinimumSiteCount, v)));
                }}
              />
              <button type="button" className="upm-stepper-btn" onClick={() => bumpSites(1)} aria-label={t('Augmenter')}>
                +
              </button>
            </div>
          </div>
        </div>

        <div className="upm-recap-wrap">
          <h3 className="upm-recap-heading">{t('Récapitulatif')}</h3>
          <div className="upm-recap">
            <div className="upm-recap-total">
              {formatEur(totals.subtotal)} <span className="upm-recap-per">/ {t('mois')}</span>
            </div>
            <div className="upm-recap-row">
              <span>{totals.line}</span>
              <span>{formatEur(totals.subtotal)}</span>
            </div>
            <div className="upm-recap-divider" />
            <div className="upm-recap-row upm-recap-row--strong">
              <span>{t('Sous total')}</span>
              <span>{formatEur(totals.subtotal)}</span>
            </div>
            <div className="upm-recap-divider" />
            <p className="upm-legal">
              {t('Abonnement mensuel renouvelé automatiquement, annulable à tout moment • Paiement sécurisé via Stripe')}
            </p>
          </div>
        </div>

        <div className="upm-actions">
          <button
            type="button"
            className="upm-btn upm-btn--primary"
            onClick={handleGoToPayment}
            disabled={isRedirecting}
          >
            {isRedirecting ? t('Redirection...') : t('Continuer vers le paiement')}
          </button>
          {paymentError && <p className="upm-error">{paymentError}</p>}
          <a href={supportHref} className="upm-btn upm-btn--ghost">
            {t("Contacter l'équipe")}
          </a>
        </div>
      </div>
    </div>
  );
}
